"use client";

import { useEffect, useRef } from "react";
import reviews from "@/content/reviews.json";

// Google reviews carousel. Data comes from content/reviews.json, a snapshot
// of the venue's Google Business Profile reviews (5 stars, with text),
// refreshed via scripts/refresh-reviews.mjs. Display only: per Google's
// guidelines, third-party review data must NOT be marked up as
// aggregateRating schema, so this component adds no structured data.
//
// Infinite loop: the card list is rendered twice; when the scroll position
// passes the end of the first copy it silently jumps back by one copy's
// width, so the track only ever appears to move right. Auto-advances one
// card at a time; pauses on hover/touch; still for prefers-reduced-motion.

const MAPS_URL =
  "https://www.google.com/maps/place/?q=place_id:ChIJE_m6cue9cEgRBD9e8WGA8uI";

const ADVANCE_MS = 3500;

function Stars({ n }) {
  return (
    <span className="review-stars" aria-label={`${n} out of 5 stars`}>
      {"★".repeat(n)}
      <span className="review-stars-dim">{"★".repeat(5 - n)}</span>
    </span>
  );
}

function Card({ r, hidden }) {
  return (
    <article className="review-card" aria-hidden={hidden || undefined}>
      <div className="review-head">
        <span className="review-avatar" aria-hidden="true">
          {r.name.trim().charAt(0).toUpperCase()}
        </span>
        <div>
          <strong className="review-name">{r.name}</strong>
          <span className="review-date">{r.date}</span>
        </div>
      </div>
      <Stars n={r.rating} />
      <p className="review-text">{r.text}</p>
      <span className="review-source">via Google</span>
    </article>
  );
}

export default function ReviewsCarousel() {
  const track = useRef(null);
  const paused = useRef(false);

  useEffect(() => {
    const el = track.current;
    if (!el) return;

    // Width of one full copy of the list (half the scrollable content).
    const copyWidth = () => el.scrollWidth / 2;

    // Seamless wrap: once past the first copy, jump back by exactly one
    // copy's width. Runs on every scroll so manual swipes loop too.
    const onScroll = () => {
      const w = copyWidth();
      if (el.scrollLeft >= w) el.scrollLeft -= w;
    };
    el.addEventListener("scroll", onScroll, { passive: true });

    const setPause = (v) => () => { paused.current = v; };
    el.addEventListener("pointerenter", setPause(true));
    el.addEventListener("pointerleave", setPause(false));
    el.addEventListener("touchstart", setPause(true), { passive: true });
    el.addEventListener("touchend", setPause(false));

    let timer = null;
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      timer = setInterval(() => {
        if (paused.current || !el.isConnected) return;
        const card = el.querySelector(".review-card");
        if (!card) return;
        const step = card.offsetWidth + 22; // card + gap
        el.scrollBy({ left: step, behavior: "smooth" });
      }, ADVANCE_MS);
    }

    return () => {
      if (timer) clearInterval(timer);
      el.removeEventListener("scroll", onScroll);
    };
  }, []);

  if (!reviews.items?.length) return null;

  return (
    <div className="reviews-shell">
      <div className="reviews-track" ref={track}>
        {reviews.items.map((r, i) => (
          <Card key={i} r={r} />
        ))}
        {reviews.items.map((r, i) => (
          <Card key={`dup-${i}`} r={r} hidden />
        ))}
      </div>
      <div className="center mt-2">
        <a href={MAPS_URL} target="_blank" rel="nofollow noopener" className="btn">
          Read All Reviews On Google
        </a>
      </div>
    </div>
  );
}
